import Breadcrumb from '@/components/feature/Breadcrumb';
import JsonLd from '@/components/feature/JsonLd';
import { useSeo, SITE_URL } from '@/hooks/useSeo';

const studioImage =
  'https://readdy.ai/api/search-image?query=cozy%20anime%20illustration%20studio%20interior%2C%20drawing%20desks%20with%20wacom%20tablets%2C%20shelves%20with%20comic%20books%20and%20art%20prints%2C%20warm%20ambient%20lighting%2C%20plants%20and%20posters%2C%20clean%20professional%20creative%20workspace%2C%20photographic%20style&width=1200&height=800&seq=about-studio&orientation=landscape';

const teamImage =
  'https://readdy.ai/api/search-image?query=cheerful%20young%20team%20of%20illustrators%20and%20editors%20collaborating%20around%20a%20large%20table%20covered%20with%20comic%20storyboards%20and%20sketches%2C%20warm%20natural%20window%20light%2C%20creative%20studio%20atmosphere%2C%20candid%20photography%20style&width=1200&height=800&seq=about-team&orientation=landscape';

const promises = [
  {
    icon: 'ri-vip-crown-line',
    title: '全站免费',
    desc: '无需注册、无需付费，所有韩漫章节均可免费在线阅读。',
  },
  {
    icon: 'ri-refresh-line',
    title: '每日更新',
    desc: '热门连载作品每日凌晨同步最新话，追更不等待。',
  },
  {
    icon: 'ri-translate-2',
    title: '专业汉化',
    desc: '与专业汉化组合作，全集中文翻译，对话还原原作味道。',
  },
  {
    icon: 'ri-shield-check-line',
    title: '纯净阅读',
    desc: '无弹窗广告干扰，加载快速稳定，专注沉浸式阅读。',
  },
  {
    icon: 'ri-customer-service-2-line',
    title: '贴心服务',
    desc: '客服 7×24 小时在线，阅读问题第一时间响应解决。',
  },
  {
    icon: 'ri-lock-2-line',
    title: '安全可靠',
    desc: '全站 HTTPS 加密传输，数据安全，浏览隐私有保障。',
  },
];

const stats = [
  { value: '5000+', label: '收录韩漫作品' },
  { value: '12万+', label: '中文章节内容' },
  { value: '800万+', label: '月活跃读者' },
  { value: '99.9%', label: '全年稳定运行' },
];

const contacts = [
  {
    icon: 'ri-mail-line',
    label: '商务合作邮箱',
    value: 'service@5177comic.com',
    href: 'mailto:service@5177comic.com',
  },
  {
    icon: 'ri-wechat-line',
    label: '官方微信公众号',
    value: '5177漫画网',
  },
  {
    icon: 'ri-qq-line',
    label: '读者交流QQ群',
    value: '5188887',
  },
  {
    icon: 'ri-map-pin-2-line',
    label: '公司地址',
    value: '中国 · 上海 · 徐汇区宜山路5177号创新大厦12层',
  },
];

const aboutLd = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  '@id': `${SITE_URL}/about#about`,
  url: `${SITE_URL}/about`,
  name: '关于我们-5177漫画网',
  description: '5177漫画网是专注于免费韩国漫画阅读的中文平台，提供全集中文翻译的热门韩漫作品，每日更新。',
  inLanguage: 'zh-CN',
  isPartOf: { '@id': `${SITE_URL}/#website` },
  about: { '@id': `${SITE_URL}/#organization` },
};

export default function AboutPage() {
  useSeo({
    title: '关于我们-5177漫画网-免费阅读韩国漫画平台',
    description:
      '5177漫画网是专注于免费韩国漫画阅读的中文平台，提供《帐篷里的秘密》等热门韩漫全集中文翻译，每日更新，全站免费。了解我们的品牌故事、服务承诺与联系方式。',
    keywords: '关于我们,5177漫画网,韩国漫画,免费阅读,韩漫平台',
    ogType: 'website',
    canonicalPath: '/about',
  });

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd data={aboutLd} />

      <div className="bg-background-100 border-b border-background-200/70">
        <div className="mx-auto max-w-7xl px-4 md:px-6 py-6 md:py-8">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />
          <h1 className="mt-4 font-display text-3xl md:text-4xl text-foreground-950">关于我们</h1>
          <p className="mt-2 max-w-3xl text-sm md:text-base text-foreground-600">
            5177漫画网 · 让每一部好漫画，都被免费看到
          </p>
        </div>
      </div>

      <main>
        {/* 品牌介绍 */}
        <section className="mx-auto max-w-7xl px-4 md:px-6 py-12 md:py-16" aria-labelledby="brand-title">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
            <div className="overflow-hidden rounded-lg hover-lift">
              <img
                src={studioImage}
                alt="5177漫画网工作室场景"
                title="5177漫画网品牌介绍"
                className="h-full w-full object-cover"
              />
            </div>
            <div>
              <h2 id="brand-title" className="font-display text-2xl md:text-3xl text-foreground-950">
                关于 <span className="text-primary-600">5177</span>漫画网
              </h2>
              <div className="mt-4 space-y-4 text-sm md:text-base leading-7 md:leading-8 text-foreground-700">
                <p>
                  5177漫画网创立于2021年，是一个专注于韩国漫画（韩漫）免费在线阅读的中文漫画平台。我们以《帐篷里的秘密》等热门作品为起点，逐步成长为拥有数千部韩漫作品、百万级读者的垂直漫画社区。
                </p>
                <p>
                  我们深知，优质的内容不应该被付费墙阻隔。因此，5177漫画网坚持「全站免费」的运营理念，联合专业汉化团队持续产出高质量中文翻译，让每一位热爱漫画的读者，都能零门槛地享受精彩的韩漫故事。
                </p>
                <p>
                  从选品入库到中文校对，从排版发布到读者反馈，我们以严谨的态度打磨每一话内容，只为大家带来流畅、纯净、沉浸的阅读体验。
                </p>
              </div>
              <dl className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
                {stats.map((s) => (
                  <div key={s.label} className="rounded-lg bg-background-100 p-4 text-center">
                    <dd className="font-display text-xl md:text-2xl text-primary-600">{s.value}</dd>
                    <dt className="mt-1 text-xs text-foreground-600">{s.label}</dt>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </section>

        {/* 团队 */}
        <section className="bg-background-100 py-12 md:py-16" aria-labelledby="team-title">
          <div className="mx-auto max-w-7xl px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
              <div className="order-2 lg:order-1">
                <h2 id="team-title" className="font-display text-2xl md:text-3xl text-foreground-950">
                  一支热爱漫画的团队
                </h2>
                <div className="mt-4 space-y-4 text-sm md:text-base leading-7 md:leading-8 text-foreground-700">
                  <p>
                    我们的团队由资深漫画编辑、专业汉化译者与产品工程师组成。他们来自五湖四海，却因为同一个热爱走到一起——让好漫画被更多人看到。
                  </p>
                  <p>
                    选书编辑每天从韩国各大连载平台筛选潜力新作；汉化团队逐格打磨对白，力求还原原作的语气与温度；运营同学则守在评论区，认真回复每一位读者的留言与建议。
                  </p>
                  <p>
                    正因如此，5177漫画网不仅是一个阅读平台，更是一个温暖的漫画爱好者社区。你的每一次追更、每一句评论，都是我们前进的动力。
                  </p>
                </div>
              </div>
              <div className="order-1 lg:order-2 overflow-hidden rounded-lg hover-lift">
                <img
                  src={teamImage}
                  alt="5177漫画网编辑团队协作场景"
                  title="5177漫画网团队介绍"
                  className="h-full w-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* 信誉承诺 */}
        <section className="mx-auto max-w-7xl px-4 md:px-6 py-12 md:py-16" aria-labelledby="promise-title">
          <div className="text-center">
            <h2 id="promise-title" className="font-display text-2xl md:text-3xl text-foreground-950">
              我们的承诺
            </h2>
            <p className="mt-3 text-sm text-foreground-600">六年如一日的坚守，只为你的阅读体验</p>
          </div>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {promises.map((p) => (
              <div key={p.title} className="rounded-lg bg-background-100 p-5 md:p-6 hover-lift">
                <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                  <i className={`${p.icon} text-2xl`} aria-hidden="true" />
                </span>
                <h3 className="mt-4 font-display text-lg text-foreground-950">{p.title}</h3>
                <p className="mt-2 text-sm leading-6 text-foreground-700">{p.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* 联系方式 */}
        <section className="mx-auto max-w-7xl px-4 md:px-6 pb-16" aria-labelledby="contact-title">
          <div className="overflow-hidden rounded-lg bg-background-100">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              <div className="p-6 md:p-10">
                <h2 id="contact-title" className="font-display text-2xl md:text-3xl text-foreground-950">
                  联系我们
                </h2>
                <p className="mt-3 text-sm leading-6 text-foreground-600">
                  无论是商务合作、内容建议还是阅读反馈，都欢迎随时联系我们。我们会认真对待每一条消息，一般在
                  24 小时内回复。
                </p>
                <ul className="mt-6 space-y-4">
                  {contacts.map((c) => (
                    <li key={c.label} className="flex items-start gap-3">
                      <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                        <i className={`${c.icon} text-lg`} aria-hidden="true" />
                      </span>
                      <div className="min-w-0">
                        <p className="text-xs text-foreground-500">{c.label}</p>
                        {c.href ? (
                          <a
                            href={c.href}
                            rel="nofollow"
                            className="mt-0.5 block text-sm font-medium text-foreground-900 hover:text-primary-600 transition-colors"
                          >
                            {c.value}
                          </a>
                        ) : (
                          <p className="mt-0.5 text-sm font-medium text-foreground-900">{c.value}</p>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="relative min-h-[280px]">
                <iframe
                  src="https://www.google.com/maps?q=Shanghai%20Xuhui%20Yishan%20Road&output=embed"
                  title="5177漫画网公司地址地图"
                  className="absolute inset-0 h-full w-full border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}